// src/theme/ThemeProvider.jsx
// Centralized theme state: reads the saved theme, validates it, defaults to
// Modern Dark, applies it as `data-theme` on <html> (so every CSS variable
// override in src/styles/themes.css takes effect immediately, no reload),
// and keeps localStorage in sync. Wraps <App /> in src/index.js -- one
// provider, one place other components (e.g. My Profile) call useTheme().
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { DEFAULT_THEME_ID, THEME_STORAGE_KEY, VALID_THEME_IDS, isValidTheme } from "./themeConfig";

function readInitialTheme() {
  try {
    const saved = localStorage.getItem(THEME_STORAGE_KEY);
    if (isValidTheme(saved)) return saved;
  } catch {
    /* localStorage unavailable (privacy mode, etc.) -- fall back below */
  }
  return DEFAULT_THEME_ID;
}

const ThemeContext = createContext({
  theme: DEFAULT_THEME_ID,
  setTheme: () => {},
  themes: VALID_THEME_IDS,
});

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(readInitialTheme);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    try {
      localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch {
      /* non-critical -- theme still applies for this session */
    }
  }, [theme]);

  const setTheme = useCallback((next) => {
    if (!isValidTheme(next)) return;
    setThemeState(next);
  }, []);

  const value = useMemo(() => ({ theme, setTheme, themes: VALID_THEME_IDS }), [theme, setTheme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  return useContext(ThemeContext);
}
